import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { getCookieDomain } from '../lib/util/cookieDomain';
import { DEV_MODE } from '../lib/util/devMode';
import { getFiGrowthBook } from '../lib/growthbook';
import { typedWindow } from '../lib/Window';
import { configActions } from '../reducers/config';
import { storeShopActions } from '../reducers/storeShop';
import { series3UpgradeShopActions } from '../reducers/series3UpgradeShop';
import { subscriptionShopActions } from '../reducers/subscriptionShop';
import { verizonShopActions } from '../reducers/verizonShop';
import { CollarProductId, series3PlusCollarId } from '../types';
import * as types from '../types';
import i18n from '../../i18n';
import { logInternalError } from '../lib/errors';

/** Countries by their ISO-2 code
 * Please ensure that the string value of the enum matches the ISO-2 code
 * @see supportedCountries for the countries that are actively supported */
export enum FiCountry {
  US = 'US',
  CA = 'CA',
  GB = 'GB',
}

export enum FiRegion {
  NA = 'NA',
  EU = 'EU',
}

export enum FiLanguage {
  EN_US = 'en-US',
  EN_CA = 'en-CA',
  FR_CA = 'fr-CA',
  EN_GB = 'en-GB',
  DE_DE = 'de-DE',
  ES_ES = 'es-ES',
  FR_FR = 'fr-FR',
  NL_NL = 'nl-NL',
}

interface LocaleContextValue {
  country: FiCountry;
  setCountry: (country: FiCountry) => void;
  language: FiLanguage;
  setLanguage: (language: FiLanguage) => void;
  region: FiRegion;
  currency: types.FiCurrency;
  /** Used for money amounts where we want the numbers after the decimal point, e.g. $99.00 */
  currencyFormatter: Intl.NumberFormat;
  /** Used for money amounts where we only want whole numbers, e.g. $99 */
  wholeNumberCurrencyFormatter: Intl.NumberFormat;
  /** List of countries supported by Fi */
  supportedCountries: FiCountry[];
  /** Flag emoji for the current country */
  flagEmoji: string;
}

export const collarProductIsSupportedInCountry = (productId: CollarProductId, country: FiCountry) => {
  if (productId === series3PlusCollarId) return true;
  return country === FiCountry.US;
};

export const supplementsProductIsSupportedInCountry = (country: FiCountry) => {
  return country === FiCountry.US;
};

export const makersAccessoriesProductIsSupportedInCountry = (country: FiCountry) => {
  return country === FiCountry.US || country === FiCountry.CA;
};

const MINI_SKU = 'M1-MUA';

const LocaleContext = React.createContext<LocaleContextValue>({
  country: FiCountry.US,
  setCountry: () => {},
  language: FiLanguage.EN_US,
  setLanguage: () => {},
  region: FiRegion.NA,
  currency: types.FiCurrency.USD,
  currencyFormatter: new Intl.NumberFormat(FiLanguage.EN_US, { style: 'currency', currency: types.FiCurrency.USD }),
  wholeNumberCurrencyFormatter: new Intl.NumberFormat(FiLanguage.EN_US, {
    style: 'currency',
    currency: types.FiCurrency.USD,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }),
  supportedCountries: [FiCountry.US, FiCountry.CA],
  flagEmoji: '🇺🇸',
});

const FI_COUNTRY_COOKIE = 'fi_country';
const VIEWER_COUNTRY_COOKIE = 'viewer_country';
const FI_LANGUAGE_COOKIE = 'fi_language';

/** Retrieve the list of supported countries from the Growthbook feature flag */
function getSupportedCountries() {
  return getFiGrowthBook().getFeatureValue<FiCountry[]>('ecom-supported-countries', [FiCountry.US]);
}

function getInitialCountry(): FiCountry {
  const fiSupportedCountries = getSupportedCountries();

  try {
    const explicit = Cookies.get(FI_COUNTRY_COOKIE)?.toUpperCase() as FiCountry | undefined;
    if (explicit && fiSupportedCountries.includes(explicit)) return explicit;
    if (explicit === FiCountry.US) return FiCountry.US;

    const viewer = Cookies.get(VIEWER_COUNTRY_COOKIE)?.toUpperCase() as FiCountry | undefined;
    if (viewer && fiSupportedCountries.includes(viewer)) return viewer;
    return FiCountry.US;
  } catch {
    return FiCountry.US;
  }
}

const validCountries = Object.values(FiCountry);

/**
 * Gets the country code from cookies, checking fi_country (explicit user choice)
 * first, then falling back to viewer_country (CloudFront GeoIP) for users who
 * never explicitly selected a country (e.g., Instagram in-app browser traffic).
 */
export function getCountryCodeFromCookie(): FiCountry {
  const explicit = Cookies.get(FI_COUNTRY_COOKIE)?.toUpperCase() as FiCountry | undefined;
  if (explicit && validCountries.includes(explicit)) {
    return explicit;
  }
  const viewer = Cookies.get(VIEWER_COUNTRY_COOKIE)?.toUpperCase() as FiCountry | undefined;
  if (viewer && validCountries.includes(viewer)) {
    return viewer;
  }
  return FiCountry.US;
}

const DEFAULT_SUPPORTED_LANGUAGES: string[] = ['EN_US', 'EN_CA', 'FR_CA', 'EN_GB'];

const LANGUAGE_TO_COUNTRY: Partial<Record<FiLanguage, FiCountry>> = {
  [FiLanguage.EN_US]: FiCountry.US,
  [FiLanguage.EN_CA]: FiCountry.CA,
  [FiLanguage.FR_CA]: FiCountry.CA,
  [FiLanguage.EN_GB]: FiCountry.GB,
};

function getGrowthBookSupportedLanguages(): FiLanguage[] {
  const raw = getFiGrowthBook().getFeatureValue<string[]>('ecom-supported-languages', DEFAULT_SUPPORTED_LANGUAGES);
  return raw.map(apiLanguageToFiLanguage).filter((l): l is FiLanguage => l != null);
}

function getSupportedLanguages(country: FiCountry): FiLanguage[] {
  const supported = getGrowthBookSupportedLanguages();
  const forCountry = supported.filter((lang) => LANGUAGE_TO_COUNTRY[lang] === country);
  if (forCountry.length > 0) return forCountry;
  return country === FiCountry.US ? [FiLanguage.EN_US] : [];
}

function detectBrowserLanguage(supported: FiLanguage[]): FiLanguage | undefined {
  const browserLang = navigator.language ?? navigator.languages?.[0];
  if (!browserLang) return undefined;

  const lang = browserLang.toLowerCase();

  if (lang.startsWith('fr') && supported.includes(FiLanguage.FR_CA)) {
    return FiLanguage.FR_CA;
  }

  if (lang.startsWith('en-ca') && supported.includes(FiLanguage.EN_CA)) {
    return FiLanguage.EN_CA;
  }

  if (lang.startsWith('en-gb') && supported.includes(FiLanguage.EN_GB)) {
    return FiLanguage.EN_GB;
  }

  return undefined;
}

function getInitialLanguage(): FiLanguage {
  const country = getInitialCountry();
  const supported = getSupportedLanguages(country);

  // Check explicit cookie preference — accept any valid FiLanguage regardless of country
  // (country restriction only applies to browser auto-detection below)
  const cookieValue = Cookies.get(FI_LANGUAGE_COOKIE) as FiLanguage | undefined;
  const allLanguages: string[] = Object.values(FiLanguage);
  if (cookieValue && allLanguages.includes(cookieValue)) {
    return cookieValue;
  }

  // Browser detection
  const browserLanguage = detectBrowserLanguage(supported);
  if (browserLanguage) {
    return browserLanguage;
  }

  // Default to the first supported language for the detected country
  return supported[0] ?? FiLanguage.EN_US;
}

// Hook to remove mini collar from cart if country is not US
// Both marketingsite and ecommerce use the same cookie, and we don't
// have a way to compare previous vs current country without extra state.
// Which is why we can't clear the entire cart on country change from tryfi.
// So we just run this effect once on mount to clean up unsupported items.
function useRemoveUnsupportedItemsFromCart(country: FiCountry) {
  const [onMount, setOnMount] = useState(true);
  const cart = useSelector((state: types.AppState) => state.storeShop.cart);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!onMount) {
      return;
    }

    if (country === FiCountry.US) {
      return;
    }

    Object.entries(cart.cartItems).forEach(([key, item]) => {
      if (item.lineItem.sku !== MINI_SKU) {
        return;
      }

      dispatch(storeShopActions.removeCartItem({ id: key }));
    });

    setOnMount(false);
  }, [onMount, country, cart, dispatch]);
}

/**
 * when en-CA users view CAD prices, use en-US locale to produce "CA$" disambiguation
 * instead of bare "$" (which en-CA locale produces since CAD is the local currency).
 * all other language/currency combos use the user's actual language locale.
 */
function getCurrencyFormatterLocale(language: FiLanguage, country: FiCountry): string {
  if (language === FiLanguage.EN_CA && getCurrencyForCountry(country) === types.FiCurrency.CAD) {
    return FiLanguage.EN_US;
  }
  return language;
}

export function getCurrencyForCountry(country: FiCountry): types.FiCurrency {
  const cadEnabled = getFiGrowthBook().getFeatureValue<boolean>('ecom-use-cad', false);
  const gbpEnabled = getFiGrowthBook().getFeatureValue<boolean>('ecom-use-gbp', false);

  if (cadEnabled && country === FiCountry.CA) {
    return types.FiCurrency.CAD;
  }
  if (gbpEnabled && country === FiCountry.GB) {
    return types.FiCurrency.GBP;
  }
  return types.FiCurrency.USD;
}

/**
 * Converts a country string (e.g. from billing address) to a FiCountry enum.
 * Returns `null` for any unsupported or unrecognized country codes.
 */
export function getFiCountryFromString(country: string) {
  const match = validCountries.find((c) => c.toString() === country.toUpperCase());
  if (match) {
    return match;
  }
  return null;
}

export function LocaleContextProvider({ children }: { children: React.ReactNode }) {
  const [country, setCountryState] = useState<FiCountry>(getInitialCountry());
  const [language, setLanguageState] = useState<FiLanguage>(getInitialLanguage());
  const dispatch = useDispatch();

  useRemoveUnsupportedItemsFromCart(country);

  const setFiCountryCookie = useCallback((value: FiCountry) => {
    Cookies.remove(FI_COUNTRY_COOKIE, { path: '/' }); // clear hostname-scoped shadow
    Cookies.set(FI_COUNTRY_COOKIE, value, {
      expires: 30, // 30 day expiration
      domain: getCookieDomain(),
      secure: !DEV_MODE,
      sameSite: 'lax',
      path: '/',
    });
  }, []);

  const setFiLanguageCookie = useCallback((value: FiLanguage) => {
    Cookies.set(FI_LANGUAGE_COOKIE, value, {
      expires: 30, // 30 day expiration
      domain: getCookieDomain(),
      secure: !DEV_MODE,
      sameSite: 'lax',
      path: '/',
    });
  }, []);

  const setCountry = useCallback(
    (newCountry: FiCountry) => {
      if (country === newCountry) return;

      setCountryState(newCountry);
      setFiCountryCookie(newCountry);

      // Update Decagon chat metadata when country changes
      if (typedWindow.duet) {
        typedWindow.duet.setMetadata({
          userRegion: newCountry,
        });
      }

      // Clear country-specific promotions and products when country changes
      const shouldValidateCart = getFiGrowthBook().getFeatureValue<boolean>('ecom-validate-product-country', false);
      for (const shop of [storeShopActions, series3UpgradeShopActions, subscriptionShopActions, verizonShopActions]) {
        if (!shouldValidateCart) {
          dispatch(shop.removeAllCartItems());
        }
        dispatch(shop.removeCoupon());
        if ('removeReferral' in shop) {
          dispatch(shop.removeReferral());
        }
        if ('removeGiftCard' in shop) {
          dispatch(shop.removeGiftCard());
        }
      }

      // Redo app config initialization since products may be affected by the country change
      dispatch(configActions.clearConfig());
    },
    [country, dispatch, setFiCountryCookie],
  );

  const setLanguage = useCallback(
    (newLanguage: FiLanguage) => {
      if (language === newLanguage) return;

      setLanguageState(newLanguage);
      setFiLanguageCookie(newLanguage);
    },
    [language, setFiLanguageCookie],
  );

  useEffect(() => {
    const existing = Cookies.get(FI_COUNTRY_COOKIE)?.toUpperCase();
    if (existing !== country) {
      setFiCountryCookie(country);
    }
  }, [country, setFiCountryCookie]);

  // Sync GrowthBook countryCode so country-targeted flags re-evaluate on navbar switch.
  useEffect(() => {
    getFiGrowthBook().setAttributes({ countryCode: country });
  }, [country]);

  useEffect(() => {
    const existing = Cookies.get(FI_LANGUAGE_COOKIE)?.toLowerCase();
    if (existing !== language) {
      setFiLanguageCookie(language);
    }
  }, [language, setFiLanguageCookie]);

  useEffect(() => {
    const target = language === FiLanguage.EN_US ? 'en' : language.toLowerCase();
    if (i18n.language !== target) {
      i18n.changeLanguage(target);
    }
  }, [language]);

  useEffect(() => {
    const otLang = language.slice(0, 2).toLowerCase();
    try {
      window.OneTrust?.changeLanguage?.(otLang);
    } catch (err) {
      logInternalError(err);
    }
  }, [language]);

  const currencyFormatter = useMemo<Intl.NumberFormat>(
    () =>
      new Intl.NumberFormat(getCurrencyFormatterLocale(language, country), {
        style: 'currency',
        currency: getCurrencyForCountry(country),
      }),
    [language, country],
  );

  const wholeNumberCurrencyFormatter = useMemo<Intl.NumberFormat>(
    () =>
      new Intl.NumberFormat(getCurrencyFormatterLocale(language, country), {
        style: 'currency',
        currency: getCurrencyForCountry(country),
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    [language, country],
  );

  const supportedCountries = getSupportedCountries();

  const flagEmoji = useMemo(() => {
    switch (country) {
      case FiCountry.CA:
        return '🇨🇦';
      case FiCountry.GB:
        return '🇬🇧';
      case FiCountry.US:
      default:
        return '🇺🇸';
    }
  }, [country]);

  const region = useMemo(() => {
    switch (country) {
      case FiCountry.CA:
      case FiCountry.US:
        return FiRegion.NA;
      case FiCountry.GB:
        return FiRegion.EU;
      default:
        return FiRegion.NA;
    }
  }, [country]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      country,
      setCountry,
      language,
      setLanguage,
      region,
      currency: getCurrencyForCountry(country),
      currencyFormatter,
      wholeNumberCurrencyFormatter,
      supportedCountries,
      flagEmoji,
    }),
    [
      country,
      setCountry,
      language,
      setLanguage,
      region,
      currencyFormatter,
      wholeNumberCurrencyFormatter,
      supportedCountries,
      flagEmoji,
    ],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}

/**
 * Maps a backend language enum name (e.g. "EN_US", "FR_CA") to the frontend
 * FiLanguage value (e.g. "en-US", "fr-CA"). Returns undefined for null/invalid input.
 */
export function apiLanguageToFiLanguage(apiLang: string | null | undefined): FiLanguage | undefined {
  if (!apiLang) return undefined;
  // Backend uses "EN_US" format; frontend uses "en-US" (BCP-47)
  const parts = apiLang.split('_');
  if (parts.length !== 2) return undefined;
  const mapped = `${parts[0].toLowerCase()}-${parts[1].toUpperCase()}`;
  return Object.values(FiLanguage).find((l) => l === mapped);
}

/**
 * Maps a backend region enum name (e.g. "US", "CA") to the frontend FiCountry value.
 * Returns undefined for null/invalid input.
 */
export function apiRegionToFiCountry(apiRegion: string | null | undefined): FiCountry | undefined {
  if (!apiRegion) return undefined;
  const upper = apiRegion.toUpperCase();
  return Object.values(FiCountry).find((c) => c === upper);
}

export function fiCurrencyToGQL(currency: types.FiCurrency) {
  switch (currency) {
    case types.FiCurrency.USD:
      return types.gqlTypes.Currency.USD;
    case types.FiCurrency.CAD:
      return types.gqlTypes.Currency.CAD;
    case types.FiCurrency.GBP:
      return types.gqlTypes.Currency.GBP;
  }
}

export default LocaleContext;
